// Module-level singleton toast store.
//
// Deliberately NOT a React context: every role home (5) plus BulkRunDock mount
// their own <GlobalToast/> adapter with no shared provider ancestor, and the
// stacked viewport must be reachable from all of them at once. A plain module
// closure + useSyncExternalStore-compatible subscribe/getSnapshot pair does
// that without any wiring at the mount sites (the 6 mounts stay byte-identical
// — see GlobalToast.js).

const QUEUE_CAP = 6;
// Exit element stays in the store (and therefore the DOM) for this long after
// dismissToast() is called, so it can absorb an iOS ghost-tap synthesized
// ~300ms after touchend on the button that triggered the dismiss. Carried
// forward verbatim from the pre-v2 GlobalToast.
export const EXIT_MS = 180;
const MIN_DWELL_REMAINING_MS = 2000;

const DISMISS_MS = { success: 4000, info: 8000, warning: 8000 };
// Errors and 'loading' never auto-dismiss — dismissMsFor returns null for both,
// which is the signal to every consumer (armDwellTimer arms nothing; ToastCard
// draws no progress bar) that there is no timer at all: a pending toast owns
// the screen until resolveToast() morphs it, and an error until the user acts.
// For success/info, a long, actionable toast (e.g. the lifecycle "activated —
// invoice staged in Drafts" message) needs time to READ and act on, not just
// notice. Extend the dwell with the message's word count, never below the
// per-type floor and never past a cap. Floors + cap land inside the
// SaaS-standard band (Material snackbars: 4-10s; short confirmations ~4-6s,
// actionable messages up to ~12s) — the word-count scaling lifts genuinely
// long messages toward the cap. The success floor is 4s (not 6s) because the
// compact card is dismissible by tap AND by swipe: the user is never stuck
// waiting one out, so the floor only has to cover a glance, and the
// word-count scaling still carries anything genuinely long past it.
const DISMISS_MS_MAX = 12000;
export function dismissMsFor(type, message) {
  if (type === 'error' || type === 'loading') return null;
  const floor = DISMISS_MS[type] ?? DISMISS_MS.success;
  const words = String(message || '').trim().split(/\s+/).filter(Boolean).length;
  const readMs = 2200 + words * 600; // ~100 wpm incl. reading + deciding + lead-in
  return Math.min(DISMISS_MS_MAX, Math.max(floor, readMs));
}

/** Ordered newest-first. Never mutated in place — always replaced so
 *  getSnapshot() identity changes exactly when consumers should re-render. */
let toasts = [];
let nextId = 1;
const listeners = new Set();
/** id -> Set<callback>, each fired exactly once when a toast fully exits (post
 *  EXIT_MS). A Set, not a single slot: a cross-adapter dedupe (two different
 *  adapters pushing an identical type+message) means TWO adapters track the
 *  same id — overwriting dropped the first adapter's onDismiss and left its
 *  parent feedback slot stale (code review P2, 2026-08-15). Each adapter's
 *  lastIdRef guard already makes the extra invocations safe. */
const dismissCallbacks = new Map();
/** id -> { timeoutId, remainingMs, paused, armedAt } — dwell bookkeeping. */
const timers = new Map();

function notify() {
  listeners.forEach((fn) => fn());
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getSnapshot() {
  return toasts;
}

function clearTimerRecord(id) {
  const rec = timers.get(id);
  if (rec?.timeoutId) clearTimeout(rec.timeoutId);
  timers.delete(id);
}

function armDwellTimer(id, ms) {
  clearTimerRecord(id);
  if (ms == null) return; // errors / loading: no auto-dismiss timer at all
  const timeoutId = setTimeout(() => dismissToast(id), ms);
  timers.set(id, { timeoutId, remainingMs: ms, paused: false, armedAt: Date.now() });
}

function startDwellFor(toast) {
  // Errors never auto-dismiss; neither does a 'loading' toast — it represents
  // work still in flight, so only resolveToast() (or an explicit dismiss) may
  // end it. A dwell timer here would silently drop the pending state while the
  // write was still running and leave the user with no result at all.
  //
  // CLEAR FIRST, then decide (code review P2): this runs on resolveToast() too,
  // where the toast may ALREADY have a timer from its previous type. A bare
  // early-return left a success's armed timer running after the card morphed
  // into an error — and that error then auto-dismissed, breaking the one
  // guarantee errors have. armDwellTimer clears too, so the non-returning path
  // is unaffected by clearing here.
  clearTimerRecord(toast.id);
  if (toast.type === 'error' || toast.type === 'loading') return;
  armDwellTimer(toast.id, dismissMsFor(toast.type, toast.message));
}

/** Pause an in-flight dwell timer, remembering the remaining time. A toast
 *  hidden in the +N overflow doesn't burn dwell: paused the instant it stops
 *  being visible, resumed (with whatever time it has left) the instant it is
 *  visible again. Hover does not pause — a resting pointer, or a hover that
 *  sticks after a tap, used to freeze a success so it never left.
 *  Idempotent: calling it on an already-paused/unarmed id is a no-op. */
export function pauseDwell(id) {
  const rec = timers.get(id);
  if (!rec || rec.paused) return;
  if (rec.timeoutId) clearTimeout(rec.timeoutId);
  const elapsed = Date.now() - rec.armedAt;
  const remainingMs = Math.max(0, rec.remainingMs - elapsed);
  timers.set(id, { timeoutId: null, remainingMs, paused: true, armedAt: null });
}

/** Resume a paused dwell timer with whatever time it had left, floored so a
 *  toast that was almost expired when paused still gives the user a beat. */
export function resumeDwell(id) {
  const rec = timers.get(id);
  if (!rec || !rec.paused) return;
  const ms = Math.max(MIN_DWELL_REMAINING_MS, rec.remainingMs);
  const timeoutId = setTimeout(() => dismissToast(id), ms);
  timers.set(id, { timeoutId, remainingMs: ms, paused: false, armedAt: Date.now() });
}

function evictOldest() {
  const live = toasts.filter((t) => t.phase !== 'exiting');
  if (live.length === 0) return;
  // Prefer evicting the oldest toast that is neither an error NOR pending:
  // errors carry information the user hasn't dismissed yet, and a 'loading'
  // toast represents a write still IN FLIGHT — evicting one would tear the
  // pending card off screen mid-request and leave resolveToast() with nothing
  // to morph (it returns false and the user never learns the outcome). Fall
  // back to the oldest overall if every live toast is protected.
  // A live `inlineAction` toast (a notice's Undo) is protected too while any
  // other toast can go: evicting it commits its pending write early and takes
  // the Undo away. If every live toast is inline, the oldest goes (its
  // dismiss callback commits — fail-safe).
  const oldestEvictable = [...live].reverse()
    .find((t) => t.type !== 'error' && t.type !== 'loading' && !t.inlineAction)
    || [...live].reverse().find((t) => t.type !== 'error' && t.type !== 'loading');
  const victim = oldestEvictable || live[live.length - 1];
  dismissToast(victim.id);
}

/**
 * Push a toast. Dedupes on identical live {type, message}: instead of adding
 * a second card, the existing one is "bumped" (bumpKey++, dwell restarted) and
 * its id is returned — ToastCard reads bumpKey to re-play the content-morph +
 * glow-pulse animation and ToastViewport never grows the stack for a repeat.
 */
export function pushToast({
  type, message, description = null, onRetry = null, retryLabel = null, inlineAction = false,
} = {}) {
  const resolvedType = String(type || 'success');
  const resolvedMessage = String(message || '');

  const existing = toasts.find(
    (t) => t.phase !== 'exiting' && t.type === resolvedType && t.message === resolvedMessage,
  );
  if (existing) {
    // IMMUTABLE bump (code review P0, 2026-08-15): replace the object AND the
    // array, never mutate in place — getSnapshot() identity is the ONLY thing
    // useSyncExternalStore re-renders on, so an in-place bump silently never
    // painted (no glow, no morph, no progress-bar restart) in the standard
    // two-adapter mount configuration.
    const bumped = { ...existing, bumpKey: existing.bumpKey + 1, description, onRetry, retryLabel, inlineAction };
    toasts = toasts.map((t) => (t.id === existing.id ? bumped : t));
    startDwellFor(bumped);
    notify();
    return bumped.id;
  }

  const id = nextId++;
  const toast = {
    id,
    type: resolvedType,
    message: resolvedMessage,
    description,
    onRetry,
    // Optional override for the retry button's label — ToastCard falls back
    // to the localized "Retry" default when this is absent (every existing
    // caller's chrome is byte-identical).
    retryLabel,
    // Notice-kit Undo (KAN-735): render the action as a quiet inline button in
    // the collapsed card's first row instead of the expanded-only retry chip.
    inlineAction,
    createdAt: Date.now(),
    phase: 'shown',
    bumpKey: 0,
    sourceKey: id,
  };
  toasts = [toast, ...toasts];
  startDwellFor(toast);
  notify();

  if (toasts.filter((t) => t.phase !== 'exiting').length > QUEUE_CAP) {
    evictOldest();
  }

  return id;
}

/**
 * Morph an existing (typically 'loading') toast IN PLACE into its resolved
 * state, so one card visibly becomes its own result instead of the pending
 * card vanishing and an unrelated one appearing beside it.
 *
 * A false return can mean SIGN-OUT (security review P2): clearAllToasts()
 * empties the store on the signedIn===false seam, so a write that settles after
 * the session ended finds nothing here. Do NOT reflexively pushToast() on false
 * — check the session first, or an in-flight save resolving post-logout paints
 * customer names and amounts onto the login screen (the 2026-07-01 BulkRunDock
 * class). Returns false when the id is gone or already exiting — the caller
 * then has nothing to morph and
 * should push a fresh toast instead.
 */
export function resolveToast(id, { type, message, description = null } = {}) {
  const existing = toasts.find((t) => t.id === id);
  if (!existing || existing.phase === 'exiting') return false;

  const resolvedType = String(type || 'success');
  const resolvedMessage = String(message || '');

  // IMMUTABLE replace, same reason as the pushToast bump (code review P0,
  // 2026-08-15): getSnapshot() identity is the ONLY thing useSyncExternalStore
  // re-renders on, so mutating the existing object would flip the type without
  // ever painting. bumpKey++ is what makes ToastCard replay its content-morph
  // crossfade — the pending copy dissolves into the result copy in one card.
  const next = {
    ...existing,
    type: resolvedType,
    message: resolvedMessage,
    description,
    bumpKey: existing.bumpKey + 1,
  };
  toasts = toasts.map((t) => (t.id === id ? next : t));
  // The resolved type owns the dwell the pending one never had: success arms
  // its floor from here, error stays until dismissed.
  startDwellFor(next);
  notify();
  return true;
}

/** Begin removing a toast: flip it to 'exiting' (ToastViewport plays the exit
 *  transition against that phase) and actually drop it from the array — then
 *  fire its registered dismiss callback — EXIT_MS later. */
export function dismissToast(id) {
  const idx = toasts.findIndex((t) => t.id === id);
  if (idx === -1) return;
  if (toasts[idx].phase === 'exiting') return; // already leaving

  clearTimerRecord(id);
  toasts = toasts.map((t) => (t.id === id ? { ...t, phase: 'exiting' } : t));
  notify();

  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    notify();
    const cbs = dismissCallbacks.get(id);
    dismissCallbacks.delete(id);
    if (cbs) cbs.forEach((cb) => cb());
  }, EXIT_MS);
}

/**
 * Dismiss every live toast EXCEPT a pending one — the action behind the
 * "Dismiss all" chip (Toast v4, 2026-09-27). The chip only appears once the
 * user has fanned the deck out, so every card it clears has been in view; it
 * replaced the error-scoped "{count} errors" bar, which is why this is no
 * longer error-only. Sign-out still has its own boundary: clearAllToasts.
 *
 * Routed through dismissToast rather than emptying the array so each card still
 * plays its exit transition and still fires its registered dismiss callbacks;
 * dropping them silently would strand the adapters' parent feedback slots
 * exactly as the cross-adapter dedupe did.
 */
export function dismissAllToasts() {
  // 'loading' is excluded and that exclusion is load-bearing (code review
  // P1): a pending toast is a write still in flight, so sweeping it would
  // strand resolveToast() with nothing to morph and the user would never
  // learn whether their write landed.
  toasts
    .filter((t) => t.phase !== 'exiting' && t.type !== 'loading')
    .forEach((t) => dismissToast(t.id));
}

/** Registers a callback fired exactly once when `id` fully exits (after the
 *  EXIT_MS ghost-tap window). Multiple adapters may track the same id after a
 *  cross-adapter dedupe — every registered callback fires. */
export function registerDismissCallback(id, cb) {
  if (typeof cb !== 'function') return;
  const cbs = dismissCallbacks.get(id) || new Set();
  cbs.add(cb);
  dismissCallbacks.set(id, cbs);
}

const sessionEndedListeners = new Set();

/** Subscribe to "the session ended" (sign-out). Notice-clear hooks use it to
 *  DROP their pending writes instead of committing them under no user.
 *  Returns an unsubscribe. */
export function onSessionEnded(cb) {
  sessionEndedListeners.add(cb);
  return () => { sessionEndedListeners.delete(cb); };
}

/** Fire the session-ended listeners. Idempotent; safe to call during render
 *  (no store notify). useSessionToastReset calls it in the render that sees
 *  signedIn === false — an ANCESTOR of the session-keyed tree, so it runs
 *  before that tree's unmount cleanups (a passive effect would run AFTER them). */
export function signalSessionEnded() {
  [...sessionEndedListeners].forEach((cb) => cb());
}

/**
 * SESSION-SCOPING (security review P1, 2026-08-15 — the 2026-07-01
 * BulkRunDock incident class): sign-out is a client-side state flip, not a
 * reload, and this store is module-level, so without an explicit clear an
 * error toast carrying tenant data (customer names, invoice numbers, $
 * amounts — errors never auto-dismiss) would survive sign-out and re-paint
 * into the NEXT sign-in on a shared device. Wired to the `signedIn === false`
 * seam via useSessionToastReset (SessionScopedShell in App.js), mirroring
 * BulkRunProvider / LifecycleActivationProvider. Also halts all dwell timers
 * so nothing fires after the session ends. `nextId` is deliberately NOT
 * reset — id reuse could re-bind a stale adapter's lastIdRef to a new toast.
 */
export function clearAllToasts() {
  signalSessionEnded();
  timers.forEach((rec) => { if (rec.timeoutId) clearTimeout(rec.timeoutId); });
  timers.clear();
  dismissCallbacks.clear();
  toasts = [];
  notify();
}

// TEST-ONLY. The store is a module-level singleton, so without this, toasts
// pushed by one test leak into the next test's render in the same file
// (dwell timers keep them "live" for far longer than any test runs). Never
// call from app code.
export function __resetToastStoreForTests() {
  clearAllToasts();
  nextId = 1;
}
